import api from "./api";
import { Item } from "../types/Item";
import { API_ENDPOINTS } from "../constants/API_ENDPOINTS";

const auctionService = {
  getAuctions: async (status: string): Promise<Item[]> => {
    const response = await api.get(
      `${API_ENDPOINTS.GET_LIVE_AUCTIONS}?status=${status}`
    );
    return response.data;
  },

  getAuction: async (id: string): Promise<Item> => {
    const response = await api.get(`${API_ENDPOINTS.GET_AUCTION}/${id}`);
    return response.data;
  },

  getLiveAuction: async (id: string): Promise<Item> => {
    const response = await api.get(
      `${API_ENDPOINTS.GET_LIVE_AUCTION_DETAIL}/${id}`
    );
    return response.data;
  },

  createItem: async (formData: FormData) => {
    const userStr = localStorage.getItem("user");
    let token = "";
    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        token = userData.token || "";
      } catch {}
    }
    const response = await fetch(API_ENDPOINTS.CREATE_AUCTION, {
      method: "POST",
      body: formData,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    });
    if (!response.ok) throw new Error("Error creating item");
    return await response.json();
  },

  getPendingAuctions: async (): Promise<Item[]> => {
    const response = await api.get("/api/adm/items/pending");
    return response.data.items;
  },

  approveItem: async (itemId: string) => {
    const response = await api.post(
      `${API_ENDPOINTS.APPROVE_PENDING_ITEM}/${itemId}/approve`
    );
    return response.data;
  },

  rejectItem: async (itemId: string) => {
    const response = await api.post(
      `${API_ENDPOINTS.REJECT_PENDING_ITEM}/${itemId}/reject`
    );
    return response.data;
  },

  deleteItem: async (itemId: string) => {
    const response = await api.delete(
      `${API_ENDPOINTS.DELETE_PENDING_ITEM}/${itemId}`
    );
    return response.data;
  },

  deleteLiveAuction: async (auctionId: string) => {
    const response = await api.delete(`/api/adm/auction_live/${auctionId}`);
    return response.data;
  },

  getPopupContent: async (language: string = "en") => {
    const response = await api.get("/api/admin/popup-content", {
      params: { language },
    });
    return response.data;
  },

  getPopupContentPublic: async (language: string = "en") => {
    const response = await api.get("/api/admin/popup-content/public", {
      params: { language },
    });
    return response.data;
  },

  updatePopupContent: async (
    data: {
      title: string;
      description: string;
      button_text: string;
      logo_url?: string;
    },
    language: string = "en"
  ) => {
    const response = await api.put("/api/admin/popup-content", data, {
      params: { language },
    });
    return response.data;
  },

  uploadPopupLogo: async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post("/api/admin/popup-logo", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  getMyPendingAuctions: async (): Promise<Item[]> => {
    const response = await api.get(API_ENDPOINTS.GET_MY_ITEMS, {
      params: { status: "pending" },
    });
    return response.data.items;
  },

  verifySession: async () => {
    return await api.get(API_ENDPOINTS.VERIFY_SESSION);
  },

  getImageUrl: (url?: string) => {
    if (!url) return "/default-auction.png";
    if (url.startsWith("http")) return url;
    if (url.startsWith("/uploads/")) return url;
    return url;
  },

  uploadImage: async (formData: FormData) => {
    const response = await api.post(API_ENDPOINTS.UPLOAD_IMAGE, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  uploadVideo: async (formData: FormData) => {
    const response = await api.post(API_ENDPOINTS.UPLOAD_VIDEO, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  getItem: async (itemId: string) => {
    const response = await api.get(`${API_ENDPOINTS.GET_ITEMS}/${itemId}`);
    return response.data;
  },

  placeBid: async (auctionId: string, amount: number) => {
    const response = await api.post(`${API_ENDPOINTS.PLACE_BID}/${auctionId}`, {
      amount,
    });
    return response.data;
  },

  updateAuction: async (id: string, formData: FormData) => {
    const response = await api.put(
      `${API_ENDPOINTS.UPDATE_LIVE_AUCTION}/${id}`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    return response.data;
  },

  updateItem: async (id: string, formData: FormData) => {
    const userStr = localStorage.getItem("user");
    let token = "";
    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        token = userData.token || "";
      } catch {}
    }
    const response = await fetch(`${API_ENDPOINTS.UPDATE_AUCTION}/${id}`, {
      method: "PUT",
      body: formData,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    });
    if (!response.ok) throw new Error("Error updating item");
    return await response.json();
  },
};

export default auctionService;
